/**
 * Admin RBAC roles (frontend mirror of the backend `@eya/shared` AdminRole).
 *
 * The backend is the source of truth and enforces every rule; this copy only
 * drives role-aware rendering so the UI never offers actions the signed-in
 * role cannot perform. A stale/forged client role can never grant access —
 * the server re-derives the role from the verified token on every request.
 *
 * Two kinds of role, mirroring the backend exactly:
 *   - ordered TIERS: support < admin < superadmin (bundles).
 *   - the GRANT role `manager`: NOT a tier. It has no level, never satisfies
 *     `roleSatisfies`, and draws all of its authority from a per-module grant
 *     map carried in the verified `adminModules` claim.
 */
export enum AdminRole {
  SUPPORT = "support",
  ADMIN = "admin",
  SUPERADMIN = "superadmin",
  MANAGER = "manager",
}

/**
 * Tier levels. `manager` is deliberately absent — it is not a tier, so it can
 * never satisfy a `>=` check (hence `Partial`).
 */
export const ADMIN_ROLE_LEVELS: Partial<Record<AdminRole, number>> = {
  [AdminRole.SUPPORT]: 1,
  [AdminRole.ADMIN]: 2,
  [AdminRole.SUPERADMIN]: 3,
};

export const TIER_ADMIN_ROLES: readonly AdminRole[] = [
  AdminRole.SUPPORT,
  AdminRole.ADMIN,
  AdminRole.SUPERADMIN,
];

export const ADMIN_ROLE_VALUES: readonly AdminRole[] = [
  AdminRole.SUPPORT,
  AdminRole.ADMIN,
  AdminRole.SUPERADMIN,
  AdminRole.MANAGER,
];

export const isAdminRole = (value: unknown): value is AdminRole =>
  typeof value === "string" &&
  (ADMIN_ROLE_VALUES as readonly string[]).includes(value);

/** Is this one of the ordered tiers (i.e. not the grant-based `manager`)? */
export const isTierAdminRole = (value: unknown): value is AdminRole =>
  isAdminRole(value) && (TIER_ADMIN_ROLES as readonly string[]).includes(value);

/**
 * True when `role` meets or exceeds `minRole` in the tier hierarchy. Fails
 * closed for unknowns AND for `manager`, matching the backend: a manager never
 * satisfies a tier requirement.
 */
export const roleSatisfies = (
  role: AdminRole | null | undefined,
  minRole: AdminRole,
): boolean => {
  if (!role || !isAdminRole(role)) return false;
  if (role === AdminRole.MANAGER || minRole === AdminRole.MANAGER) {
    return role === minRole;
  }
  const level = ADMIN_ROLE_LEVELS[role];
  const required = ADMIN_ROLE_LEVELS[minRole];
  if (level === undefined || required === undefined) return false;
  return level >= required;
};

/** Grantable admin modules (mirror of the backend `AdminModule`). */
export enum AdminModule {
  CONTENT = "content",
  MARKETPLACE = "marketplace",
  CRM = "crm",
  ANALYTICS = "analytics",
  SUBSCRIPTIONS = "subscriptions",
  USERS = "users",
}

export const ADMIN_MODULE_VALUES: readonly AdminModule[] = [
  AdminModule.CONTENT,
  AdminModule.MARKETPLACE,
  AdminModule.CRM,
  AdminModule.ANALYTICS,
  AdminModule.SUBSCRIPTIONS,
  AdminModule.USERS,
];

export type AccessLevel = "none" | "read" | "write";

export const ACCESS_LEVEL_VALUES: readonly AccessLevel[] = [
  "none",
  "read",
  "write",
];

/** Absent module => `none`. */
export type AdminModulePermissions = Partial<Record<AdminModule, AccessLevel>>;

const ACCESS_LEVEL_RANK: Record<AccessLevel, number> = {
  none: 0,
  read: 1,
  write: 2,
};

export const isAccessLevel = (value: unknown): value is AccessLevel =>
  typeof value === "string" &&
  (ACCESS_LEVEL_VALUES as readonly string[]).includes(value);

/**
 * Can this principal reach `module` at `level`?
 *
 * Tier roles carry every module as a bundle (support reads, admin+ writes) —
 * they have no grant map. A manager is decided solely by its grants. Mirrors
 * `enforceMatrix` closely enough for menu/action gating; the server is still
 * the authority (it also pins some routes at superadmin, which this ignores).
 */
export const canAccessModule = (
  role: AdminRole | null | undefined,
  modulePermissions: AdminModulePermissions | null | undefined,
  module: AdminModule,
  level: Exclude<AccessLevel, "none"> = "read",
): boolean => {
  if (!role) return false;
  if (role === AdminRole.MANAGER) {
    const granted = modulePermissions?.[module];
    if (!isAccessLevel(granted)) return false;
    return ACCESS_LEVEL_RANK[granted] >= ACCESS_LEVEL_RANK[level];
  }
  return level === "read"
    ? roleSatisfies(role, AdminRole.SUPPORT)
    : roleSatisfies(role, AdminRole.ADMIN);
};
